import type { JsonSchema, JsonValue, MapDocument, ValidationIssue } from "daedalus-core";
import { GATE } from "../editor/type-names.ts";
import { AUTO_CLOSE_FIELDS } from "./auto-close.ts";
import type { Rules } from "../rules/types.ts";

/**
 * The village lock as content (P1.8-T4, ADR-058's amendment of 2026-10-10): a gate's two authored fields, the lock they
 * make, whether a thing's shared facts open it, the Issues a half-authored lock raises, and the keys the map server
 * sends for the page to word. The lock is shared: one key spent opens it for every character (ADR-120 section 7). Pure.
 */

/**
 * The name of the guard a locked gate's `open` requires: a thing guard the map server registers.
 */
export const UNLOCKED_GUARD = "unlocked";

/**
 * The key a refused `open` at a locked gate sends in `interaction.text`, which the page words.
 */
export const LOCKED_LINE = "thing.locked";

/**
 * The key a press sends when its key was spent and the gate could not then open, which the page words.
 */
export const UNLOCKED_LINE = "thing.unlocked";

/**
 * The name of the list the editor offers a gate's key from: the rules' items.
 */
export const ITEM_LOOKUP = "items";

/**
 * The longest lock id, in characters.
 */
export const MAX_LOCK_ID_CHARS = 64;

/**
 * The field holding a gate's lock id, and the one holding the rules id of the item that opens it.
 */
const LOCK_ID_FIELD = "lockId";
const KEY_FIELD = "key";

/**
 * A gate's fields as the editor's inspector draws them: its Lock, empty for none, its Key, chosen from the rules'
 * items, and the fields of how it unlocks and closes itself (`AUTO_CLOSE_FIELDS`).
 */
export const GATE_SCHEMA: JsonSchema = {
  type: "object",
  properties: {
    [LOCK_ID_FIELD]: { type: "string", title: "Lock", default: "", maxLength: MAX_LOCK_ID_CHARS },
    [KEY_FIELD]: { type: "string", title: "Key", default: "", lookup: ITEM_LOOKUP },
    ...AUTO_CLOSE_FIELDS,
  },
};

/**
 * A lock as a gate's fields make it: its id and the rules id of its key, empty when none is named.
 */
export interface Lock {
  readonly lockId: string;
  readonly key: string;
}

/**
 * The lock a thing's fields make.
 *
 * @param fields The thing's behaviour fields.
 * @returns The lock, or null when the lock id is missing, empty or not text.
 * @example
 * lockOf({ lockId: "north", key: "village_key" });
 * // { lockId: "north", key: "village_key" }
 */
export function lockOf(fields: { readonly [key: string]: JsonValue | unknown }): Lock | null {
  const lockId = fields[LOCK_ID_FIELD];
  if (typeof lockId !== "string" || lockId === "") {
    return null;
  }
  const key = fields[KEY_FIELD];
  return { lockId, key: typeof key === "string" ? key : "" };
}

/**
 * Whether a thing's fields hold a lock id that is present and not text, which only a hand edit writes: such a thing
 * makes no lock a key can spend on, opens for no facts, and raises `lock.bad-id`.
 *
 * @param fields The thing's behaviour fields.
 * @returns True when the lock id is present and not a string.
 */
function badLockId(fields: { readonly [key: string]: unknown }): boolean {
  const lockId = fields[LOCK_ID_FIELD];
  return lockId !== undefined && typeof lockId !== "string";
}

/**
 * Whether a thing's shared facts open its lock: true for a thing with no lock, and for one whose `unlockedBy` is an
 * object naming the lock id the thing has now, so a publish that changes the id locks it again. A lock id that is
 * present and not text opens for nothing.
 *
 * @param fields The thing's behaviour fields.
 * @param facts The thing's shared facts.
 * @returns True when nothing locks it.
 */
export function opensLock(fields: { readonly [key: string]: unknown }, facts: { readonly [key: string]: unknown }): boolean {
  if (badLockId(fields)) {
    return false;
  }
  const lock = lockOf(fields);
  if (lock === null) {
    return true;
  }
  const by = facts["unlockedBy"];
  return typeof by === "object" && by !== null && (by as { readonly lockId?: unknown }).lockId === lock.lockId;
}

/**
 * The Issues a map's gates raise in the editor and at a compile: a Lock that is not text, a Lock with no Key, a Key with
 * no Lock, a Key the rules hold no item for, and a Lock longer than `MAX_LOCK_ID_CHARS`, each refused with its fix after
 * the semicolon.
 *
 * @param map The map.
 * @param rules The rules whose items a key names.
 * @returns The issues, in document order.
 */
export function lockIssues(map: MapDocument, rules: Pick<Rules, "items">): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (prop.behaviour?.type !== GATE) {
        continue;
      }
      const fields = prop.behaviour.fields;
      const lockId = typeof fields[LOCK_ID_FIELD] === "string" ? (fields[LOCK_ID_FIELD] as string) : "";
      const key = typeof fields[KEY_FIELD] === "string" ? (fields[KEY_FIELD] as string) : "";
      const at = { mapId: map.id, layerId: layer.id, propId: prop.id };
      if (badLockId(fields)) {
        issues.push({ ...at, kind: "lock.bad-id", message: `prop ${prop.id} is a Gate whose Lock is not text; name its Lock again in the inspector, or empty it` });
      } else if (lockId !== "" && key === "") {
        issues.push({ ...at, kind: "lock.no-key", message: `prop ${prop.id} is a Gate with a Lock and no Key; choose its Key, or empty its Lock` });
      } else if (lockId === "" && key !== "") {
        issues.push({ ...at, kind: "lock.no-lock", message: `prop ${prop.id} is a Gate with a Key and no Lock; name its Lock, or empty its Key` });
      } else if (key !== "" && !rules.items.has(key)) {
        issues.push({ ...at, kind: "lock.unknown-key", message: `prop ${prop.id} is a Gate whose Key ${key} is no item of the rules; choose another Key` });
      } else if (lockId.length > MAX_LOCK_ID_CHARS) {
        issues.push({ ...at, kind: "lock.long-id", message: `prop ${prop.id} is a Gate whose Lock is longer than ${MAX_LOCK_ID_CHARS} characters; shorten it` });
      }
    }
  }
  return issues;
}
